/**
 * Structured event stream (PRD §5.6, §7.2).
 *
 * The renderer wants to react to discrete moments in a battle:
 * weapon-fire, hits, kills, terrain transitions, agent reasoning.
 * The raw `actions.events` JSONL payload is loosely typed (`unknown[]`
 * per the parser types). This module narrows the most useful kinds.
 *
 * Wire shape from the engine (server/mecharena/state.py::GameEvent):
 *
 *   {
 *     "turn": <int>,
 *     "kind": <EventKind string>,    // e.g. "fire", "damage", "staggered"
 *     "actor": <PlayerId int>,        // PlayerId.A=1, PlayerId.B=2
 *     "payload_json": "<json string>" // GameEvent.payload, stringified
 *   }
 *
 * `payload_json` carries the per-event detail (`tool`, `zone`, `amount`,
 * `target`, …). We parse + merge it onto the raw object before reading.
 *
 * Actor semantics differ per event kind (see resolver.py):
 *   - FIRE / WEAPON_DUD / DAMAGE / EXPOSED → actor = attacker (shooter).
 *     Victim is the OTHER player.
 *   - STAGGERED / KNOCKED_DOWN / SHUTDOWN_SKIP / RESTART / CRIT_* /
 *     AMMO_EXPLOSION / GHOST_HEAT / FLUSH_COOLANT → actor = the affected
 *     player (i.e. the victim).
 *   - AMS_CANCEL → actor = defender (the one whose AMS fired).
 *
 * Engine kind strings (state.py::EventKind) are the source of truth.
 * Accept the engine name AND the historical short alias so older replay
 * archives still parse.
 *
 * `tool` lands as the engine `ToolId.name` (e.g. `"MEDIUM_LASER"`); we
 * keep it as `number | string` so older archives with integer ids still
 * decode.
 *
 * Zone names from the engine are UPPERCASE (`"FRONT_CT"`); we lowercase
 * them to match the `Zone` type and the visual-engine zone vocabulary.
 */

import type { Hex, ParsedBattle } from "@pilot-protocol/replay-parser";

export type Damage = "ballistic" | "energy" | "explosive" | "missile" | "other";

/** Engine zone strings (state.py::Zone). */
export type Zone =
  | "head"
  | "front_ct"
  | "rear_ct"
  | "l_side"
  | "r_side"
  | "l_leg"
  | "r_leg";

export type RenderEvent =
  | {
      kind: "weapon_fire";
      tick: number;
      shooter: "A" | "B";
      /** Victim side — consumer derives the target hex from the
       *  battle's frame state at this tick. */
      target: "A" | "B";
      tool: number | string;
      damageType: Damage;
    }
  | {
      /** A shot was fired but missed (raw `weapon_dud` event). Rendered as a
       *  desaturated tracer so misses read distinct from connecting hits. */
      kind: "weapon_miss";
      tick: number;
      shooter: "A" | "B";
      target: "A" | "B";
      tool: number | string;
      damageType: Damage;
    }
  | {
      kind: "hit";
      tick: number;
      victim: "A" | "B";
      zone: Zone;
      damage: number;
      damageType: Damage;
    }
  | { kind: "kill"; tick: number; victim: "A" | "B"; cause: string }
  | { kind: "terrain_change"; tick: number; position: Hex; from: number; to: number }
  | { kind: "stagger"; tick: number; victim: "A" | "B" }
  | { kind: "knockdown"; tick: number; victim: "A" | "B" }
  | { kind: "shutdown"; tick: number; victim: "A" | "B" }
  | { kind: "restart"; tick: number; victim: "A" | "B" }
  | { kind: "ammo_explosion"; tick: number; victim: "A" | "B" }
  | { kind: "exposed"; tick: number; victim: "A" | "B"; zone: Zone }
  | { kind: "crit_engine"; tick: number; victim: "A" | "B" }
  | { kind: "crit_gyro"; tick: number; victim: "A" | "B" }
  | { kind: "crit_actuator"; tick: number; victim: "A" | "B"; zone: Zone }
  | { kind: "crit_tool_destroyed"; tick: number; victim: "A" | "B"; tool: number | string }
  | { kind: "ams_cancel"; tick: number; victim: "A" | "B"; shooter: "A" | "B" }
  | { kind: "collision"; tick: number }
  | { kind: "ghost_heat"; tick: number; victim: "A" | "B" }
  | { kind: "flush_coolant"; tick: number; victim: "A" | "B" }
  | {
      kind: "los_blocked";
      tick: number;
      shooter: "A" | "B";
      target: "A" | "B";
    }
  | { kind: "other"; tick: number; payload: unknown };

interface RawEvent {
  kind?: string;
  type?: string;
  actor?: number | "A" | "B";
  shooter?: number | "A" | "B";
  victim?: number | "A" | "B";
  /** Some engine payloads carry the target as a player int. */
  target?: number | "A" | "B" | Hex;
  tool?: number | string;
  zone?: string;
  damage?: number;
  /** Newer resolver damage payload uses `amount` not `damage`. */
  amount?: number;
  damage_type?: string;
  origin?: Hex;
  position?: Hex;
  from?: number;
  to?: number;
  cause?: string;
  reason?: string;
  payload_json?: string;
  payload?: unknown;
}

/**
 * Engine `actor` is a `PlayerId` IntEnum (A=1, B=2). Old archives /
 * tests sometimes pass 0/"A"/"B" — accept all three.
 */
function sideFrom(v: number | "A" | "B" | undefined): "A" | "B" {
  if (v === "A") return "A";
  if (v === "B") return "B";
  if (v === 1 || v === 0) return "A";
  return "B";
}

function otherSide(s: "A" | "B"): "A" | "B" {
  return s === "A" ? "B" : "A";
}

function damageType(t: string | undefined): Damage {
  switch (t) {
    case "ballistic":
    case "energy":
    case "explosive":
    case "missile":
      return t;
    default:
      return "other";
  }
}

function zoneOf(z: string | undefined): Zone {
  const lower = (z ?? "").toLowerCase();
  switch (lower) {
    case "head":
    case "front_ct":
    case "rear_ct":
    case "l_side":
    case "r_side":
    case "l_leg":
    case "r_leg":
      return lower;
    default:
      return "front_ct";
  }
}

/**
 * Merge `payload_json` (the engine's wire shape) and `payload` (older /
 * test-friendly shape) onto the raw event. After this every event field
 * the renderer reads lives at the top level, whether the source was the
 * stringified payload or an already-flattened dict.
 */
function flatten(raw: unknown): RawEvent {
  if (!raw || typeof raw !== "object") return {};
  const r = raw as RawEvent & { [extra: string]: unknown };
  let payload: Record<string, unknown> | null = null;
  if (typeof r.payload_json === "string") {
    try {
      const parsed = JSON.parse(r.payload_json);
      if (parsed && typeof parsed === "object") {
        payload = parsed as Record<string, unknown>;
      }
    } catch {
      // Malformed payload_json — ignore; treat as no detail.
    }
  } else if (r.payload && typeof r.payload === "object") {
    payload = r.payload as Record<string, unknown>;
  }
  return payload ? { ...payload, ...r } : r;
}

/**
 * Best-effort normalization of one raw event into a `RenderEvent`. The
 * resolver's event shape isn't a hard contract; we accept what we
 * recognize and stash the rest under `other` so the timeline panel can
 * still tick a marker.
 *
 * Reminder: `actor` semantics flip per event kind (see file header).
 */
export function normalizeEvent(raw: unknown, tick: number): RenderEvent {
  if (!raw || typeof raw !== "object") {
    return { kind: "other", tick, payload: raw };
  }
  const r = flatten(raw);
  const k = r.kind ?? r.type ?? "";
  const actor = sideFrom(r.actor);
  const dmgType = damageType(r.damage_type);

  switch (k) {
    case "weapon_fire":
    case "shot":
    case "fire": {
      // actor = shooter; victim = OTHER player. The renderer derives
      // hex positions from frame state at consume time, so we only
      // need to record sides.
      const shooter = r.shooter !== undefined ? sideFrom(r.shooter) : actor;
      const target =
        r.target !== undefined && (typeof r.target === "number" || typeof r.target === "string")
          ? sideFrom(r.target as number | "A" | "B")
          : otherSide(shooter);
      return {
        kind: "weapon_fire",
        tick,
        shooter,
        target,
        tool: r.tool ?? 0,
        damageType: dmgType,
      };
    }
    case "weapon_dud":
    case "weapon_miss":
    case "miss": {
      const shooter = r.shooter !== undefined ? sideFrom(r.shooter) : actor;
      const target =
        r.target !== undefined && (typeof r.target === "number" || typeof r.target === "string")
          ? sideFrom(r.target as number | "A" | "B")
          : otherSide(shooter);
      return {
        kind: "weapon_miss",
        tick,
        shooter,
        target,
        tool: r.tool ?? 0,
        damageType: dmgType,
      };
    }
    case "hit":
    case "damage": {
      // Engine emits with actor = attacker; the wounded player is the
      // OTHER side. The resolver uses `amount`; older archives used
      // `damage`. Zone names are UPPERCASE on the wire (`"FRONT_CT"`).
      const attacker = r.shooter !== undefined ? sideFrom(r.shooter) : actor;
      const victim =
        r.victim !== undefined ? sideFrom(r.victim) : otherSide(attacker);
      const amount =
        typeof r.amount === "number"
          ? r.amount
          : typeof r.damage === "number"
            ? r.damage
            : 0;
      return {
        kind: "hit",
        tick,
        victim,
        zone: zoneOf(r.zone),
        damage: amount,
        damageType: dmgType,
      };
    }
    case "kill":
    case "destroyed":
      return {
        kind: "kill",
        tick,
        victim:
          r.victim !== undefined ? sideFrom(r.victim) : otherSide(actor),
        cause: String(r.cause ?? r.reason ?? "unknown"),
      };
    case "terrain_change":
    case "hex_state":
      if (r.position && r.from != null && r.to != null) {
        return {
          kind: "terrain_change",
          tick,
          position: r.position,
          from: r.from,
          to: r.to,
        };
      }
      break;
    // actor = the affected player (victim).
    case "stagger":
    case "staggered":
      return { kind: "stagger", tick, victim: actor };
    case "knockdown":
    case "knocked_down":
      return { kind: "knockdown", tick, victim: actor };
    case "shutdown":
    case "shutdown_skip":
      return { kind: "shutdown", tick, victim: actor };
    case "restart":
      return { kind: "restart", tick, victim: actor };
    case "ammo_explosion":
      return { kind: "ammo_explosion", tick, victim: actor };
    // exposed: actor = attacker; victim is OTHER player.
    case "exposed":
      return {
        kind: "exposed",
        tick,
        victim:
          r.victim !== undefined ? sideFrom(r.victim) : otherSide(actor),
        zone: zoneOf(r.zone),
      };
    case "crit_engine":
      return { kind: "crit_engine", tick, victim: actor };
    case "crit_gyro":
      return { kind: "crit_gyro", tick, victim: actor };
    case "crit_actuator":
      return {
        kind: "crit_actuator",
        tick,
        victim: actor,
        zone: zoneOf(r.zone),
      };
    case "crit_tool_destroyed":
      return {
        kind: "crit_tool_destroyed",
        tick,
        victim: actor,
        tool: r.tool ?? 0,
      };
    case "ams_cancel":
      // actor = defender (whose AMS fired); shooter is the one whose
      // missile got intercepted.
      return {
        kind: "ams_cancel",
        tick,
        victim: actor,
        shooter:
          r.shooter !== undefined ? sideFrom(r.shooter) : otherSide(actor),
      };
    case "collision":
      return { kind: "collision", tick };
    case "ghost_heat":
      return { kind: "ghost_heat", tick, victim: actor };
    case "flush_coolant":
      return { kind: "flush_coolant", tick, victim: actor };
    case "los_blocked": {
      const shooter = r.shooter !== undefined ? sideFrom(r.shooter) : actor;
      const target =
        r.target !== undefined && (typeof r.target === "number" || typeof r.target === "string")
          ? sideFrom(r.target as number | "A" | "B")
          : otherSide(shooter);
      return { kind: "los_blocked", tick, shooter, target };
    }
  }
  return { kind: "other", tick, payload: raw };
}

/** Flatten every per-turn event in a battle into a single time-ordered list. */
export function eventStream(battle: ParsedBattle): RenderEvent[] {
  const out: RenderEvent[] = [];
  for (const t of battle.turns) {
    for (const raw of t.events) out.push(normalizeEvent(raw, t.turn));
  }
  return out;
}

/** Events fired at exactly this tick (cheap; matches list is short). */
export function eventsAtTick(stream: RenderEvent[], tick: number): RenderEvent[] {
  return stream.filter((e) => e.tick === tick);
}

/** Find the next tick (strictly greater than current) carrying any event of `kinds`. */
export function nextEventTick(
  stream: RenderEvent[],
  current: number,
  kinds: ReadonlyArray<RenderEvent["kind"]>,
): number | null {
  const set = new Set<string>(kinds);
  for (const e of stream) {
    if (e.tick > current && set.has(e.kind)) return e.tick;
  }
  return null;
}

/** Find the previous tick (strictly less than current) carrying any event of `kinds`. */
export function prevEventTick(
  stream: RenderEvent[],
  current: number,
  kinds: ReadonlyArray<RenderEvent["kind"]>,
): number | null {
  const set = new Set<string>(kinds);
  let found: number | null = null;
  for (const e of stream) {
    if (e.tick >= current) break;
    if (set.has(e.kind)) found = e.tick;
  }
  return found;
}
